import { useEffect, useState } from "react";
import type { Message, ReplyTarget } from "../types";
import { isReplyableEmail } from "../utils/replyable";
import type { ComposerTab } from "../components/constants";

interface UseReplyTargetParams {
  conversationId?: string;
  activeChannel: ComposerTab;
  /** Message to pre-seed the reply banner from (the `?reply_to` deep link). */
  replyToMessageId?: string;
  messages: Message[];
  contactName: string;
  onReplyConsumed?: () => void;
}

/**
 * The composer's reply banner: resolves `replyToMessageId` against the loaded
 * thread once it arrives, and drops the banner whenever the conversation or
 * channel changes.
 *
 * Only replyable emails can be replied to; anything else (a chat message, a
 * missing id) is reported as consumed so the host can strip its deep-link
 * param.
 */
export const useReplyTarget = ({
  conversationId,
  activeChannel,
  replyToMessageId,
  messages,
  contactName,
  onReplyConsumed
}: UseReplyTargetParams) => {
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);

  useEffect(() => {
    setReplyTarget(null);
  }, [conversationId, activeChannel]);

  useEffect(() => {
    if (!replyToMessageId || messages.length === 0) return;

    const parent = messages.find((m) => m.id === replyToMessageId);

    if (!parent || !isReplyableEmail(parent)) {
      onReplyConsumed?.();
      return;
    }

    const isOutbound = parent.direction === "outbound";
    setReplyTarget({
      messageId: parent.id,
      fromName: isOutbound
        ? parent.sender_identifier || "You"
        : parent.sender_identifier || contactName,
      fromAddress: parent.email_details?.from_address || "",
      preview: (parent.content_plain || "").slice(0, 160)
    });
    onReplyConsumed?.();
  }, [replyToMessageId, messages, contactName, onReplyConsumed]);

  return { replyTarget, setReplyTarget };
};
